import React, { useState, useEffect } from "react";
import { usePermissions } from "../../../../PermissionsProvider";
import { DatasetData } from "../../../../model_gen/dataset";
import { DatasetService } from "../../../../services/DatasetService";
import { IntentService } from "../../../../services/IntentService";
import DatasetGenerateConfig from "./DatasetGenerateConfig";
import { GenerationVariable, normalizeGenerationVariableFromMeta } from "./generate-config/types";
import { mergeDatasetGenerateMeta } from "./generate-config/datasetGenerateMeta";
import dayjs from "dayjs";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import { Label } from "../../../ui/label";
import { Skeleton } from "../../../ui/skeleton";
import { Input } from "../../../ui/input";
import { Slider } from "../../../ui/slider";
import { Progress } from "../../../ui/progress";
import {
  DatabaseIcon,
  CalendarIcon,
  PercentIcon,
  InboxIcon,
} from "lucide-react";

type DatasetDataSourceType = "manual" | "production" | "s3" | "generate";

interface DatasetSamplingProps {
  datasetData: DatasetData;
  ignoredApplicationIds: number[];
  onChange: (data: Partial<DatasetData>) => void;
  configStep: number;
  setConfigStep: (step: number) => void;
}

const DatasetSampling: React.FC<DatasetSamplingProps> = ({
  datasetData,
  ignoredApplicationIds,
  onChange,
  configStep,
  setConfigStep,
}) => {
  const { apiKey } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [totalRequests, setTotalRequests] = useState(0);
  const [resolvedIntentGroupId, setResolvedIntentGroupId] = useState<number | undefined>(undefined);

  const meta: any = mergeDatasetGenerateMeta(
    (datasetData.meta || {}) as Record<string, unknown>
  );
  const dataSourceType: DatasetDataSourceType =
    (meta.datasource_type as DatasetDataSourceType) || "production";

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // ── Production request count ────────────────────────────────────────

  const fetchRequestCount = () => {
    if (
      !apiKey ||
      !(
        (datasetData.base_type === "intent_group" && datasetData.intent_group_id) ||
        (datasetData.base_type === "application_intent" && datasetData.application_id && datasetData.intent_id)
      )
    )
      return;

    setLoading(true);
    const igId = datasetData.base_type === "intent_group" ? datasetData.intent_group_id : null;
    const appId = datasetData.base_type === "application_intent" ? datasetData.application_id : null;
    const intId = datasetData.base_type === "application_intent" ? datasetData.intent_id : null;

    DatasetService.getEstimatedDatasetRequestCount(
      igId,
      appId,
      intId,
      datasetData.date_start,
      datasetData.date_end,
      ignoredApplicationIds,
      setLoading,
      apiKey
    ).subscribe(
      (response: any) => {
        const total = response && typeof response.count === "number" ? response.count : 0;
        setTotalRequests(total);
        setLoading(false);
        onChange({ total_requests: total });
      },
      () => setLoading(false)
    );
  };

  useEffect(() => {
    if (dataSourceType === "production") fetchRequestCount();
  }, [
    dataSourceType,
    datasetData.base_type,
    datasetData.intent_group_id,
    datasetData.application_id,
    datasetData.intent_id,
    datasetData.date_start,
    datasetData.date_end,
    ignoredApplicationIds,
    apiKey,
  ]);

  useEffect(() => {
    if (datasetData.date_start) setStartDate(dayjs(datasetData.date_start).format("YYYY-MM-DD"));
    if (datasetData.date_end) setEndDate(dayjs(datasetData.date_end).format("YYYY-MM-DD"));
  }, [datasetData.date_start, datasetData.date_end]);

  useEffect(() => {
    if (datasetData.base_type === "intent_group" && datasetData.intent_group_id) {
      setResolvedIntentGroupId(datasetData.intent_group_id);
    } else if (datasetData.base_type === "application_intent" && datasetData.application_id && datasetData.intent_id && apiKey) {
      IntentService.getIntentGroupByApplicationAndIntent(
        datasetData.application_id,
        datasetData.intent_id,
        undefined,
        apiKey
      ).subscribe(
        (resp: any) => {
          if (resp?.id) setResolvedIntentGroupId(resp.id);
        },
        () => {}
      );
    }
  }, [datasetData.base_type, datasetData.intent_group_id, datasetData.application_id, datasetData.intent_id, apiKey]);

  const handleDateRangeChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
    onChange({
      date_start: start ? dayjs(start).startOf("day").valueOf() : undefined,
      date_end: end ? dayjs(end).endOf("day").valueOf() : undefined,
    });
  };

  const calculateSampleSize = () => {
    if (datasetData.sample_type === "all") return totalRequests;
    if (datasetData.sample_type === "random" && datasetData.sample_percentage) {
      return Math.round((totalRequests * datasetData.sample_percentage) / 100);
    }
    return 0;
  };

  const sampleSize = calculateSampleSize();
  const samplePercentageOfTotal = totalRequests > 0 ? (sampleSize / totalRequests) * 100 : 0;

  // ── Render helpers ──────────────────────────────────────────────────

  const headerTitle =
    dataSourceType === "generate"
      ? "Generation Configuration"
      : dataSourceType === "production"
      ? "Sampling Configuration"
      : "Configuration";

  const headerDescription =
    dataSourceType === "generate"
      ? "Configure the teacher, generation mode, and templates for your synthetic dataset"
      : dataSourceType === "production"
      ? "Configure how production data is sampled for your dataset"
      : "Review your data source configuration";

  // ── Render ──────────────────────────────────────────────────────────

  return (
    <div className="space-y-8">
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-gc-demi-bold text-foreground">{headerTitle}</h2>
        <p className="text-muted-foreground font-gc-reg">{headerDescription}</p>
      </div>

      {/* ── Production Sampling ── */}
      {dataSourceType === "production" && (
        <>
          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                <DatabaseIcon className="w-5 h-5 text-maitai-green" />
                Sampling Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-gc-med text-foreground">Sampling Method</Label>
                  <span className="text-red-500 text-sm">*</span>
                </div>
                <RadioGroup
                  value={datasetData.sample_type}
                  onValueChange={(value) => onChange({ sample_type: value as "all" | "random" | "none" })}
                  className="space-y-4"
                >
                  <div className="flex items-start space-x-3">
                    <RadioGroupItem value="all" id="all" />
                    <div className="space-y-1">
                      <Label htmlFor="all" className="text-sm font-gc-med text-foreground cursor-pointer">Use All Requests</Label>
                      <p className="text-xs text-muted-foreground font-gc-reg">Include all available requests in the dataset</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <RadioGroupItem value="random" id="random" />
                    <div className="space-y-1">
                      <Label htmlFor="random" className="text-sm font-gc-med text-foreground cursor-pointer">Random Sample</Label>
                      <p className="text-xs text-muted-foreground font-gc-reg">Select a random percentage of requests</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <RadioGroupItem value="none" id="none" />
                    <div className="space-y-1">
                      <Label htmlFor="none" className="text-sm font-gc-med text-foreground cursor-pointer">None (Empty Dataset)</Label>
                      <p className="text-xs text-muted-foreground font-gc-reg">Create an empty dataset with no requests</p>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {(datasetData.sample_type === "all" || datasetData.sample_type === "random") && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-maitai-green" />
                    <Label className="text-sm font-gc-med text-foreground">Filter by Date Range</Label>
                    <span className="text-xs text-muted-foreground">(Optional)</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Start Date</Label>
                      <Input type="date" value={startDate} onChange={(e) => handleDateRangeChange(e.target.value, endDate)} className="w-full" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">End Date</Label>
                      <Input type="date" value={endDate} onChange={(e) => handleDateRangeChange(startDate, e.target.value)} className="w-full" />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Leave both fields empty to include all dates</p>
                </div>
              )}

              {datasetData.sample_type === "random" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <PercentIcon className="w-4 h-4 text-maitai-green" />
                      <Label className="text-sm font-gc-med text-foreground">Sample Percentage</Label>
                      <span className="text-red-500 text-sm">*</span>
                    </div>
                    <span className="text-sm font-gc-med text-maitai-green">{datasetData.sample_percentage}%</span>
                  </div>
                  <div className="space-y-2">
                    <Slider
                      value={[datasetData.sample_percentage || 100]}
                      onValueChange={(value) => onChange({ sample_percentage: value[0] })}
                      max={100}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>1%</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Percentage of requests to include in the dataset</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Dataset Size Preview */}
          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                <InboxIcon className="w-5 h-5 text-maitai-green" />
                Dataset Size Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Sample Progress</span>
                  <span className="font-gc-med text-foreground">{Math.round(samplePercentageOfTotal)}%</span>
                </div>
                <Progress value={samplePercentageOfTotal} className="h-2" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border-border bg-card/50">
                  <CardContent className="p-6">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <DatabaseIcon className="w-4 h-4 text-maitai-green" />
                        <span className="text-sm font-gc-med text-muted-foreground">Total Available</span>
                      </div>
                      <div className="text-2xl font-gc-demi-bold text-maitai-green">
                        {loading ? <Skeleton className="h-8 w-20" /> : <>{totalRequests.toLocaleString()} <span className="text-lg text-muted-foreground">requests</span></>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border bg-card/50">
                  <CardContent className="p-6">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <InboxIcon className="w-4 h-4 text-maitai-green" />
                        <span className="text-sm font-gc-med text-muted-foreground">Dataset Size</span>
                      </div>
                      <div className="text-2xl font-gc-demi-bold text-maitai-green">
                        {sampleSize.toLocaleString()} <span className="text-lg text-muted-foreground">requests</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* ── Generate with AI ── */}
      {dataSourceType === "generate" && (
        <DatasetGenerateConfig
          config={{
            generation_mode: (meta.generation_mode || "open_ended") as "open_ended" | "structured",
            generation_teacher_type: (meta.generation_teacher_type || "model") as "model" | "workflow" | "agent",
            generation_model: (meta.generation_model as string) || "",
            generation_seed_model: (meta.generation_seed_model as string) || "",
            generation_size: (meta.generation_size as number) || 2000,
            generation_size_tier: (meta.generation_size_tier as string) || "medium",
            generation_max_tokens: (meta.generation_max_tokens as number) ?? null,
            generation_domains: (meta.generation_domains as string[]) || [],
            generation_context: (meta.generation_context as string) || "",
            generation_include_reasoning: (meta.generation_include_reasoning as boolean) ?? false,
            generation_response_instructions: (meta.generation_response_instructions as string) || "",
            generation_request_system_prompt:
              Object.prototype.hasOwnProperty.call(meta, "generation_request_system_prompt") ||
              Object.prototype.hasOwnProperty.call(meta, "generation_teacher_system_prompt")
                ? String((meta as { generation_request_system_prompt?: string }).generation_request_system_prompt ?? "")
                : ((meta.generation_system_prompt as string) || ""),
            generation_teacher_system_prompt:
              Object.prototype.hasOwnProperty.call(meta, "generation_request_system_prompt") ||
              Object.prototype.hasOwnProperty.call(meta, "generation_teacher_system_prompt")
                ? String((meta as { generation_teacher_system_prompt?: string }).generation_teacher_system_prompt ?? "")
                : "",
            generation_user_template: (meta.generation_user_template as string) || "",
            generation_user_example: (meta.generation_user_example as string) || "",
            generation_response_example: (meta.generation_response_example as string) || "",
            generation_difficulty_cues: (meta.generation_difficulty_cues as string) || "",
            generation_seed_instructions: (meta.generation_seed_instructions as string) || "",
            generation_workflow_input: (meta.generation_workflow_input as string) || "",
            generation_teacher_response_value_path:
              (meta.generation_teacher_response_value_path as string) || "",
            generation_response_schema: meta.generation_response_schema || [],
            generation_computed_placeholders: (meta.generation_computed_placeholders as any[]) || [],
            generation_variables: (
              Array.isArray(meta.generation_variables) ? meta.generation_variables : []
            ).map((row: unknown) => normalizeGenerationVariableFromMeta(row as Partial<GenerationVariable> & { args?: string[] })),
            generation_use_prod_seeds: (meta.generation_use_prod_seeds as boolean) ?? false,
            generation_prod_seed_ids: (meta.generation_prod_seed_ids as string[]) || [],
          }}
          onChange={(partial) => {
            onChange({ meta: { ...datasetData.meta, datasource_type: "generate", ...partial } });
          }}
          intentGroupId={resolvedIntentGroupId}
          configStep={configStep}
          setConfigStep={setConfigStep}
        />
      )}

      {/* ── Manual / S3 — nothing extra to configure ── */}
      {(dataSourceType === "manual" || dataSourceType === "s3") && (
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <InboxIcon className="w-5 h-5 text-maitai-green" />
              <div>
                <p className="text-sm font-gc-med text-foreground">
                  {dataSourceType === "manual" ? "File uploaded" : "S3 source validated"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {dataSourceType === "manual"
                    ? `${((meta.manual_dataset_size as number) || 0).toLocaleString()} records ready`
                    : "Your S3 data will be imported when the dataset is created."}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DatasetSampling;
export type { DatasetSamplingProps };
